import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Checkbox } from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { Terms } from '@/modules/perseo-billing/components';
import {
  schema,
  yupResolver
} from '@/modules/perseo-billing/containers/payment-methods/components/cencopay/cencopay.schema';
import { CencoPayFooter } from '@/modules/perseo-billing/containers/payment-methods/components/cencopay/cencopay-footer.component';
import { CencoPayPromotionBanner } from '@/modules/perseo-billing/containers/payment-methods/components/cencopay/cencopay-promotion-banner.component';
import { useClearPaymentMethod } from '@/modules/perseo-billing/hooks';
import type { ISinglePayment } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  setPaymentAvailable,
  setSelectedPaymentMethods
} from '@/modules/perseo-billing/store/slices/billing';
import { Card } from '@/modules/perseo-billing/ui/react/src/components/beta';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src/ui-hooks';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

const paymentMethodToAdd: ISinglePayment = {
  paymentProcessor: PaymentProcessor.CENCOPAY
};

export const CencoPay = (): ReactElement => {
  const billing = useAppSelector((state) => state.billing);
  const { selectedPaymentMethods, isPaymentAvailable } = billing || {};

  const isMobile = useMediaQuery('lg');
  const dispatch = useAppDispatch();

  const cencoPayForm = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      termsAndConditions: true
    }
  });

  const { register, setValue, watch } = cencoPayForm;
  const { isValid } = cencoPayForm.formState;

  const { clearPayment } = useClearPaymentMethod();

  useEffect(() => {
    if (!isValid) return;

    if (!selectedPaymentMethods || !isPaymentAvailable) {
      dispatch(
        setSelectedPaymentMethods({
          payments: [paymentMethodToAdd]
        })
      );
      dispatch(setPaymentAvailable(true));
    }
  }, [dispatch, isValid, selectedPaymentMethods, isPaymentAvailable]);

  useEffect(() => {
    if (!isValid) {
      clearPayment();
    }
  }, [clearPayment, isValid]);

  return (
    <FormProvider {...cencoPayForm}>
      <Card>
        <form className="co-flex co-flex-col co-px-24px co-py-16px co-w-full co-gap-4">
          <div className="co-flex co-flex-col md:co-flex-row lg:co-flex-col xl:co-flex-row co-gap-16px co-justify-between">
            <p
              className={`co-w-full co-h-24px co-leading-24px ${
                isMobile ? 'co-text-body1' : 'co-text-h4'
              }`}
              tabIndex={0}
            >
              Pagarás desde la app de CencoPay.
            </p>
            <CencoPayPromotionBanner />
          </div>
          <Checkbox
            label={<Terms />}
            defaultChecked={watch('termsAndConditions')}
            {...register('termsAndConditions', {
              onChange: (e) => {
                setValue('termsAndConditions', e.target.checked);
              }
            })}
          />
          {!isMobile && <CencoPayFooter />}
        </form>
      </Card>
      <LocalHookFormDevtool control={cencoPayForm?.control} />
    </FormProvider>
  );
};
